import {
  type HotelQuestionnaireState,
  type HotelQuestionnaireAction,
  type StepsLoadingState,
} from '../types/hotelQuestionnaireTypes';

// Initial state
export const initialState: HotelQuestionnaireState = {
  stepsData: null,
  stepsLoading: 'idle' as StepsLoadingState,
  stepsError: null,
  questionnaireData: null,
  questionnaireLoading: false,
  questionnaireError: null,
  answers: {},
  draftAnswers: {},
  sources: {},
  sourcesData: null,
  sourcesLoading: false,
  sourcesError: null,
  targetSourceGroup: null,
  reviews: {},
  draftReviews: {},
  readonlyQuestionnaireData: null,
  readonlyQuestionnaireLoading: false,
  readonlyQuestionnaireError: null,
  submissions: [],
  submissionsLoading: false,
  submissionsError: null,
  selectedSubmissionId: null,
  hotelId: null,
  questionnaireType: null,
  isSwitchingType: false,
};
// Reducer
export const hotelQuestionnaireReducer = (
  state: HotelQuestionnaireState,
  action: HotelQuestionnaireAction
): HotelQuestionnaireState => {
  switch (action.type) {
    case 'SET_HOTEL_ID':
      return { ...state, hotelId: action.payload };

    case 'SET_STEPS_LOADING':
      return { ...state, stepsLoading: action.payload };

    case 'SET_STEPS_DATA':
      return { ...state, stepsData: action.payload, stepsError: null };

    case 'SET_STEPS_ERROR':
      return { ...state, stepsError: action.payload };

    case 'SET_QUESTIONNAIRE_LOADING':
      return { ...state, questionnaireLoading: action.payload };

    case 'SET_QUESTIONNAIRE_DATA':
      return {
        ...state,
        questionnaireData: action.payload,
        questionnaireError: null,
      };

    case 'SET_QUESTIONNAIRE_ERROR':
      return {
        ...state,
        questionnaireError: action.payload,
      };

    case 'SET_ANSWERS':
      return { ...state, answers: action.payload };

    case 'SET_DRAFT_ANSWERS':
      return { ...state, draftAnswers: action.payload };

    case 'CLEAR_DRAFT_ANSWERS':
      return { ...state, draftAnswers: {} };

    case 'APPLY_DRAFT_ANSWERS':
      // console.log('Applying draft answers to main state:', {
      //   currentAnswers: state.answers,
      //   draftAnswers: state.draftAnswers,
      //   combinedResult: { ...state.answers, ...state.draftAnswers },
      // });
      return {
        ...state,
        answers: { ...state.answers, ...state.draftAnswers },
        draftAnswers: {},
      };

    case 'UPDATE_ANSWER':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: {
            questionId: action.payload.questionId,
            value: action.payload.value,
            note: action.payload.note,
          },
        },
      };

    case 'UPDATE_DRAFT_ANSWER':
      // console.log('Updating draft answer:', action.payload);
      return {
        ...state,
        draftAnswers: {
          ...state.draftAnswers,
          [action.payload.questionId]: {
            questionId: action.payload.questionId,
            value: action.payload.value,
            note: action.payload.note,
          },
        },
      };

    case 'UPDATE_SOURCES':
      return {
        ...state,
        sources: {
          ...state.sources,
          [action.payload.stepKey]: action.payload.questions,
        },
      };

    case 'UPDATE_CURRENT_STEP':
      return {
        ...state,
        stepsData: state.stepsData
          ? { ...state.stepsData, currentStepKey: action.payload }
          : null,
      };

    case 'SET_SOURCES_LOADING':
      return { ...state, sourcesLoading: action.payload };

    case 'SET_SOURCES_DATA':
      return { ...state, sourcesData: action.payload, sourcesError: null };

    case 'SET_TARGET_SOURCE_GROUP':
      return { ...state, targetSourceGroup: action.payload };

    case 'SET_SOURCES_ERROR':
      return { ...state, sourcesError: action.payload };

    case 'SET_READONLY_QUESTIONNAIRE_LOADING':
      return { ...state, readonlyQuestionnaireLoading: action.payload };

    case 'SET_READONLY_QUESTIONNAIRE_DATA':
      return {
        ...state,
        readonlyQuestionnaireData: action.payload,
        readonlyQuestionnaireError: null,
      };

    case 'SET_READONLY_QUESTIONNAIRE_ERROR':
      return { ...state, readonlyQuestionnaireError: action.payload };

    case 'UPDATE_QUESTION_ATTACHMENTS':
      // console.log('Attachment payload', action.payload);
      return {
        ...state,
        sourcesData: state.sourcesData
          ? {
              ...state.sourcesData,
              groups:
                state.sourcesData.groups?.map((group: any) => {
                  // console.log('Processing group:', group.key);

                  return {
                    ...group,
                    metadata: {
                      ...group.metadata,
                      fields:
                        group.metadata.fields?.map((field: any) => {
                          // console.log('Processing field:', field.id);

                          // Check if this is the main field we're updating
                          if (
                            !field.hasSubQuestions &&
                            field.id === action.payload.questionId
                          ) {
                            // console.log('Found main field match:', field.id);
                            return {
                              ...field,
                              attachments: action.payload.attachments,
                            };
                          }

                          // Special handling for radio-grid custom questions
                          // For radio-grid, attachments should be stored on the main question
                          if (
                            field.type === 'custom' &&
                            (field as any).component === 'radio-grid' &&
                            field.id === action.payload.questionId
                          ) {
                            return {
                              ...field,
                              attachments: action.payload.attachments,
                            };
                          }

                          // Check subQuestions if they exist (for non-radio-grid questions)
                          if (
                            field.hasSubQuestions &&
                            field.subQuestions &&
                            field.subQuestions.length > 0
                          ) {
                            // Check if any subQuestion has the target ID
                            const targetSubQuestionIndex =
                              field.subQuestions.findIndex(
                                (subQ: any) =>
                                  subQ.id === action.payload.questionId
                              );

                            if (targetSubQuestionIndex !== -1) {
                              // Create a new array with the updated subQuestion
                              const updatedSubQuestions = [
                                ...field.subQuestions,
                              ];
                              updatedSubQuestions[targetSubQuestionIndex] = {
                                ...updatedSubQuestions[targetSubQuestionIndex],
                                attachments: action.payload.attachments,
                              };
                              return {
                                ...field,
                                subQuestions: updatedSubQuestions,
                              };
                            }
                          }

                          return field;
                        }) || [],
                    },
                  };
                }) || [],
            }
          : null,
      };

    case 'UPDATE_QUESTION_UPLOADED_FILENAMES':
      return {
        ...state,
        questionnaireData: state.questionnaireData
          ? (() => {
              // Handle grouped questionnaire data
              if (
                'hasGroups' in state.questionnaireData &&
                state.questionnaireData.hasGroups
              ) {
                return {
                  ...state.questionnaireData,
                  groups:
                    state.questionnaireData.groups?.map(group => ({
                      ...group,
                      metadata: {
                        ...group.metadata,
                        fields:
                          group.metadata.fields?.map(field => {
                            // console.log('Processing field for field:', field);
                            // Check if this is the main field we're updating
                            if (
                              !field.hasSubQuestions &&
                              field.id === action.payload.questionId
                            ) {
                              // console.log(
                              //   'Found main field match for filenames:',
                              //   field.id
                              // );
                              return {
                                ...field,
                                uploaded_filenames:
                                  action.payload.uploadedFilenames,
                              };
                            }

                            // Special handling for radio-grid custom questions
                            // For radio-grid, uploaded_filenames should be stored on the main question
                            if (
                              field.type === 'custom' &&
                              (field as any).component === "radio-grid" &&
                              field.id === action.payload.questionId
                            ) {
                              // console.log(
                              //   'Found radio-grid main field match for filenames:',
                              //   field.id
                              // );
                              return {
                                ...field,
                                uploaded_filenames:
                                  action.payload.uploadedFilenames,
                              };
                            }

                            // Check subQuestions if they exist (for non-radio-grid questions)
                            if (
                              field.hasSubQuestions &&
                              field.subQuestions &&
                              field.subQuestions.length > 0
                            ) {
                              const targetSubQuestionIndex =
                                field.subQuestions.findIndex(
                                  subQ => subQ.id === action.payload.questionId
                                );

                              if (targetSubQuestionIndex !== -1) {
                                // console.log(
                                //   'Found subQuestion match for filenames at index:',
                                //   targetSubQuestionIndex
                                // );
                                const updatedSubQuestions = [
                                  ...field.subQuestions,
                                ];
                                updatedSubQuestions[targetSubQuestionIndex] = {
                                  ...updatedSubQuestions[
                                    targetSubQuestionIndex
                                  ],
                                  uploaded_filenames:
                                    action.payload.uploadedFilenames,
                                };

                                return {
                                  ...field,
                                  subQuestions: updatedSubQuestions,
                                };
                              }
                            }

                            return field;
                          }) || [],
                      },
                    })) || [],
                };
              }

              // Handle standard questionnaire data
              else {
                return {
                  ...state.questionnaireData,
                  metadata: {
                    ...state.questionnaireData.metadata,
                    fields:
                      state.questionnaireData.metadata.fields?.map(
                        (question: any) =>
                          question.id === action.payload.questionId
                            ? {
                                ...question,
                                uploaded_filenames:
                                  action.payload.uploadedFilenames,
                              }
                            : question
                      ) || [],
                  },
                };
              }
            })()
          : null,
      };

    case 'SET_SUBMISSIONS_LOADING':
      return { ...state, submissionsLoading: action.payload };

    case 'SET_SUBMISSIONS':
      return { ...state, submissions: action.payload, submissionsError: null };

    case 'SET_SUBMISSIONS_ERROR':
      return { ...state, submissionsError: action.payload };

    case 'SET_SELECTED_SUBMISSION_ID':
      return { ...state, selectedSubmissionId: action.payload };

    case 'SET_QUESTIONNAIRE_TYPE':
      return { ...state, questionnaireType: action.payload };

    case 'SET_SWITCHING_TYPE':
      return { ...state, isSwitchingType: action.payload };

    case 'RESET_STATE':
      return initialState;

    case 'UPDATE_QUESTION_EXCLUSION_STATES':
      if (!state.questionnaireData) return state;

      const changedQuestionId = action.payload; // The question whose answer changed
      const combinedAnswers = { ...state.answers, ...state.draftAnswers };

      // Handle grouped questionnaire data
      if (
        'hasGroups' in state.questionnaireData &&
        state.questionnaireData.hasGroups
      ) {
        const {
          findGroupedQuestionById,
          updateDependentQuestionsExclusionState,
        } = require('../../utils/groupedDependencyUtils');

        const groupedData = state.questionnaireData as any;

        // Get all questions from all groups
        const allQuestions =
          groupedData.groups?.flatMap((g: any) => g.metadata.fields || []) ||
          [];

        // Find the question that changed
        const changedQuestion = findGroupedQuestionById(
          allQuestions,
          changedQuestionId
        );

        if (!changedQuestion || !changedQuestion.dependency_list || changedQuestion.dependency_list.length === 0) {
          // No dependency_list, nothing to update
          return state;
        }

        // Get the answer for the changed question
        const changedAnswer = combinedAnswers[changedQuestionId];

        // Update only the dependent questions
        const updatedDependentQuestions = updateDependentQuestionsExclusionState(
          changedQuestion,
          changedAnswer,
          allQuestions
        );

        // Create a map of updated questions by ID for quick lookup
        const updatedQuestionsMap = new Map(
          updatedDependentQuestions.map((q: any) => [q.id, q])
        );

        // Update the groups by replacing updated questions
        const updatedGroups =
          groupedData.groups?.map((group: any) => ({
            ...group,
            metadata: {
              ...group.metadata,
              fields:
                group.metadata.fields?.map((field: any) => {
                  const updatedField = updatedQuestionsMap.get(field.id);
                  return updatedField || field;
                }) || [],
            },
          })) || [];

        return {
          ...state,
          questionnaireData: {
            ...state.questionnaireData,
            groups: updatedGroups,
          } as any,
        };
      }

      // Handle standard questionnaire data
      else {
        const {
          findQuestionById,
          updateDependentQuestionsExclusionState,
        } = require('../../utils/dependencyUtils');

        const standardData = state.questionnaireData as any;
        const allQuestions = standardData.metadata.fields || [];

        // Find the question that changed
        const changedQuestion = findQuestionById(
          allQuestions,
          changedQuestionId
        );

        if (!changedQuestion || !changedQuestion.dependency_list || changedQuestion.dependency_list.length === 0) {
          // No dependency_list, nothing to update
          return state;
        }

        // Get the answer for the changed question
        const changedAnswer = combinedAnswers[changedQuestionId];

        // Update only the dependent questions
        const updatedDependentQuestions = updateDependentQuestionsExclusionState(
          changedQuestion,
          changedAnswer,
          allQuestions
        );

        // Create a map of updated questions by ID for quick lookup
        const updatedQuestionsMap = new Map(
          updatedDependentQuestions.map((q: any) => [q.id, q])
        );

        // Recursively update questions in the tree
        const updateQuestionsInTree = (questions: any[]): any[] => {
          return questions.map(question => {
            const updatedQuestion = updatedQuestionsMap.get(question.id);
            
            if (updatedQuestion) {
              return updatedQuestion;
            }

            // Check subQuestions
            if (question.subQuestions && question.subQuestions.length > 0) {
              return {
                ...question,
                subQuestions: updateQuestionsInTree(question.subQuestions),
              };
            }

            return question;
          });
        };

        const updatedFields = updateQuestionsInTree(allQuestions);

        return {
          ...state,
          questionnaireData: {
            ...state.questionnaireData,
            metadata: {
              ...standardData.metadata,
              fields: updatedFields,
            },
          } as any,
        };
      }

    case 'CLEANUP_EXCLUDED_QUESTION_ANSWERS':
      if (!state.questionnaireData) return state;

      const currentCombinedAnswers = { ...state.answers, ...state.draftAnswers };

      // Handle grouped questionnaire data
      if (
        'hasGroups' in state.questionnaireData &&
        state.questionnaireData.hasGroups
      ) {
        const {
          cleanupExcludedGroupedQuestionAnswersSync,
        } = require('../../utils/groupedDependencyUtils');

        const groupedData = state.questionnaireData as any;
        const allGroupedQuestions =
          groupedData.groups?.flatMap((g: any) => g.metadata.fields || []) ||
          [];

        const cleanedAnswers = cleanupExcludedGroupedQuestionAnswersSync(
          currentCombinedAnswers,
          allGroupedQuestions
        );

        // Split cleaned answers back into main and draft
        const cleanedMainAnswers: Record<string, any> = {};
        const cleanedDraftAnswers: Record<string, any> = {};

        Object.keys(cleanedAnswers).forEach(questionId => {
          if (state.draftAnswers[questionId]) {
            cleanedDraftAnswers[questionId] = cleanedAnswers[questionId];
          } else if (state.answers[questionId]) {
            cleanedMainAnswers[questionId] = cleanedAnswers[questionId];
          }
        });

        return {
          ...state,
          answers: cleanedMainAnswers,
          draftAnswers: cleanedDraftAnswers,
        };
      }

      // Handle standard questionnaire data
      else {
        const {
          cleanupExcludedQuestionAnswersSync,
        } = require('../../utils/dependencyUtils');

        const standardData = state.questionnaireData as any;
        const cleanedAnswers = cleanupExcludedQuestionAnswersSync(
          currentCombinedAnswers,
          standardData.metadata.fields
        );

        // Split cleaned answers back into main and draft
        const cleanedMainAnswers: Record<string, any> = {};
        const cleanedDraftAnswers: Record<string, any> = {};

        Object.keys(cleanedAnswers).forEach(questionId => {
          if (state.draftAnswers[questionId]) {
            cleanedDraftAnswers[questionId] = cleanedAnswers[questionId];
          } else if (state.answers[questionId]) {
            cleanedMainAnswers[questionId] = cleanedAnswers[questionId];
          }
        });

        return {
          ...state,
          answers: cleanedMainAnswers,
          draftAnswers: cleanedDraftAnswers,
        };
      }

    case 'SET_REVIEWS':
      return { ...state, reviews: action.payload };

    case 'SET_DRAFT_REVIEWS':
      return { ...state, draftReviews: action.payload };

    case 'CLEAR_DRAFT_REVIEWS':
      return { ...state, draftReviews: {} };

    case 'APPLY_DRAFT_REVIEWS':
      // console.log('Applying draft reviews to main state:', {
      //   currentReviews: state.reviews,
      //   draftReviews: state.draftReviews,
      //   combinedResult: { ...state.reviews, ...state.draftReviews },
      // });
      return {
        ...state,
        reviews: { ...state.reviews, ...state.draftReviews },
        draftReviews: {},
      };

    case 'UPDATE_REVIEW':
      return {
        ...state,
        reviews: {
          ...state.reviews,
          [action.payload.questionId]: {
            questionId: action.payload.questionId,
            approved: action.payload.approved,
            reasonId: action.payload.reasonId,
          },
        },
      };

    case 'UPDATE_DRAFT_REVIEW':
      // console.log('Updating draft review:', action.payload);
      return {
        ...state,
        draftReviews: {
          ...state.draftReviews,
          [action.payload.questionId]: {
            questionId: action.payload.questionId,
            approved: action.payload.approved,
            reasonId: action.payload.reasonId,
          },
        },
      };

    default:
      return state;
  }
};
